
import { defineComponent, PropType } from '@vue/composition-api';
import { mapGetters, mapActions } from 'vuex';
import { BetOdd } from '../../../../../types/sport';
import BetStatusIcon from './BetStatusIcon.vue';

interface CashoutBetV2 {
  _id: string;
  code: string;
  type: 'single' | 'multiple' | 'integral' | 'system';
  bet_context: 'prematch' | 'live' | 'mixed' | 'antepost';
  result: 'pending' | 'win' | 'lose' | 'void' | 'cancelled';
  status: 'accepted' | 'review' | 'finished';
  amount_real: number;
  winning_real?: number;
  created_at: string;
  currencySymbol: string;
  odds: BetOdd[];
  cashout?: boolean;
  cashoutAvailable?: boolean;
  cashoutValue?: number | null;
  isCheckingCashout?: boolean;
  isProcessingCashout?: boolean;
  [key: string]: any;
}

// Costante per le etichette statistiche per sport
const STATISTICS_LABELS: Record<string | number, Record<number, string>> = {
  // FOOTBALL/SOCCER
  8412615417: {
    1: 'Corners',
    2: 'Shots On Target',
    3: 'Shots Off Target',
    4: 'Attacks',
    5: 'Dangerous Attacks',
    6: 'Yellow Card',
    7: 'Red Card',
    8: 'Penalties',
    9: 'Goal',
    10: 'Substitutions',
    11: 'Possession',
    12: 'Fouls',
    13: 'Free Kicks',
    14: 'Goal Kicks',
    15: 'Offsides',
    16: 'Blocked Shots',
    17: 'Throw Ins',
    18: 'Woodwork Shots',
    24: 'Own Goal',
    27: 'Score',
    96: 'Goalkeeper Saves',
    102: 'Player Minutes Played',
    148: 'Pass Attempts',
    149: 'Pass Completions',
    1028: 'Player Blocked Shots',
    1036: 'Player Assists',
    2001: 'Player Goals',
    2003: 'Player Tackles',
    2004: 'Player Saves',
    2005: 'Player Shots On Target',
    2006: 'Player Shots Off Target',
    2007: 'Player Fouls',
    2034: 'Assists',
    2154: 'Player Offsides',
    2155: 'Player Yellow Card',
    2173: 'Player Red Card',
    2228: 'Tackles',
    2255: 'Player Total Shots',
    2289: 'Header Goals',
    2344: 'Woodwork Shots'
  },
  // TENNIS
  3467550448: {
    20: 'Aces',
    21: 'Double Faults',
    22: 'Service Points',
    23: 'Break Points',
    27: 'Score',
    34: 'First Serve Wins',
    55: 'Number Of Points',
    63: 'Second Serve Return Points Won',
    65: 'Service Game Won',
    67: 'Break Points Saved',
    68: 'Second Serve Wins',
    70: 'First Serve Return Points Won',
    78: 'Return Games Played',
    2044: 'Service Points Won',
    2045: 'Receiver Points Won',
    2065: 'Max Points In Row',
    2066: 'First Serve Percentage',
    2068: 'Service Errors',
    2090: 'Max Games In Row',
    2094: 'Second Serve Percentage',
    2095: 'Break Points Won Percentage',
    2147: 'Total Games Won',
    2194: 'Successful 1st Serves (Raw)',
    2195: '1st Serve Points Won (%)',
    2196: '2nd Serve Points Won (%)',
    2197: 'Successful 2nd Serves (Raw)',
    2198: '1st Serve Return Points Won (%)',
    2199: '2nd Serve Return Points Won (%)',
    2202: 'Tie Breaks Won (Raw)',
    2205: '1st Serve Attempts (Raw)',
    2206: '2nd Serve Attempts (Raw)',
    2207: '1st Serve Points Played (Raw)',
    2208: '2nd Serve Points Played (Raw)',
    2209: 'Break Points Attempts (Raw)',
    2231: '1st Serve Return Points Played',
    2232: '2nd Serve Return Points Played',
    2299: 'Service Games Played (Raw)'
  },
  // BASKETBALL
  5177137473: {
    12: 'Fouls',
    27: 'Score',
    28: '2 Points',
    30: '3 Points',
    31: 'Timeouts',
    32: 'Free Throws',
    98: 'Player Technical Fouls',
    99: 'Player Blocks Against',
    100: 'Player Defensive Rebounds',
    101: 'Player Offensive Rebounds',
    102: 'Player Minutes Played',
    103: 'Player Field Goals Made',
    104: 'Player Total Rebounds',
    105: 'Player Personal Fouls',
    106: 'Player Free Throws Made',
    107: 'Player Field Goals Attempted',
    108: 'Player 2-Point FG Made',
    109: 'Player Steals',
    110: 'Player Free Throws Attempted',
    111: 'Player 3-Point FG Attempted',
    112: 'Player Turnovers',
    113: 'Player 3-Point FG Made',
    114: 'Player 2-Point FG Attempted',
    1025: 'Player +/- Points',
    1028: 'Player Blocked Shots',
    1033: 'Player Points',
    1036: 'Player Assists',
    2007: 'Player Fouls',
    2029: 'Rebounds',
    2030: 'Defensive Rebounds',
    2031: 'Offensive Rebounds',
    2034: 'Assists',
    2035: 'Steals',
    2214: 'Player Free Throws Made (%)',
    2222: 'Player 2 Pointers Made (%)',
    2223: 'Player 3 Pointers Made (%)',
    2224: 'Player FG Made (%)'
  },
  // VOLLEYBALL
  2908586663: {
    16: 'Blocked Shots',
    20: 'Aces',
    27: 'Score'
  },
  // BASEBALL
  4105083646: {
    27: 'Score',
    33: 'Hits',
    93: 'Runs',
    1009: 'Pitcher Base on Balls',
    1010: 'Pitcher Strikeouts',
    1022: 'Batter Strikeouts',
    1023: 'Batter Left on Base',
    2080: 'Doubles',
    2081: 'Triples',
    2082: 'Home Runs',
    2083: 'Strike Outs',
    2096: 'Errors',
    2097: 'Stolen Bases'
  }
};

export default defineComponent({
  name: 'CashoutBetDetailModal',
  components: {
    BetStatusIcon
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bet: {
      type: Object as PropType<CashoutBetV2 | null>,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      getPlatformSettings: 'settings/getPlatformSettings',
      getOddsType: 'odds/getOddsType',
      getDetailMyBets: 'user/getDetailMyBets'
    }),
    isBetCancelled(): boolean {
      return this.bet?.result === 'cancelled';
    },
    detailOdds(): BetOdd[] {
      const detail = this.getDetailMyBets;
      if (detail && detail.odds && Array.isArray(detail.odds)) {
        return detail.odds;
      }
      return this.bet?.odds || [];
    }
  },
  methods: {
    ...mapActions({
      resetCart: 'cart/resetCart',
      setOddsInCart: 'cart/setOddsInCart',
      setCreatedAtCart: 'cart/setCreatedAtCart',
      refreshCart: 'cart/refreshCart'
    }),
    t(key: string): string {
      return this.$t(key) as string;
    },
    formatDate(date: string | number): string {
      return new Date(date).toLocaleString();
    },
    formatOddDate(time: string | number): string {
      const date = new Date(time);
      return `${date.toLocaleDateString('en-GB', {
        month: 'numeric',
        day: 'numeric'
      })} ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    },
    formatAmount(amount: number | string): string {
      return parseFloat(String(amount)).toFixed(2);
    },
    formatNumber(value: number | null | undefined): string {
      return parseFloat(String(value || 0)).toFixed(2);
    },
    formatTimeActual(time: number | string): string {
      return parseFloat(String(time)).toFixed(0);
    },
    getWinning(bet: CashoutBetV2): string {
      const betAny = bet as any;
      switch (bet.type) {
        case 'single':
        case 'multiple':
          const multipleDetail = betAny._multiple_detail;
          if (multipleDetail && multipleDetail.winning_total !== undefined) {
            return `${bet.currencySymbol} ${this.formatAmount(
              multipleDetail.winning_total
            )}`;
          }
          return `${bet.currencySymbol} 0.00`;
        case 'integral':
          const integralDetail = betAny._integral_detail;
          if (
            integralDetail &&
            integralDetail.winning_total_max !== undefined
          ) {
            return `${bet.currencySymbol} ${this.formatAmount(
              integralDetail.winning_total_max
            )}`;
          }
          return `${bet.currencySymbol} 0.00`;
        case 'system':
          const systemDetail = betAny._system_detail;
          if (systemDetail && systemDetail.winning_all_total !== undefined) {
            return `${bet.currencySymbol} ${this.formatAmount(
              systemDetail.winning_all_total
            )}`;
          }
          return `${bet.currencySymbol} 0.00`;
        default:
          return `${bet.currencySymbol} 0.00`;
      }
    },
    getSpecifiers(spec: string): string {
      if (!spec || !spec.includes('#')) return '';
      const split = spec.split('#');
      if (split[1]?.includes(':')) {
        return ` (${split[1]})`;
      } else if (split[1]?.includes('(') && split[1]?.includes('.')) {
        return ` [${split[1]}]`;
      } else if (split[1]) {
        return ` ${split[1]}`;
      }
      return split.length > 2 ? ` ${split[2]}` : '';
    },
    getOddStatus(
      odd: BetOdd
    ): 'pending' | 'win' | 'lose' | 'void' | 'cancelled' {
      if (this.bet?.cashout && !odd.result) return 'void';
      if (this.bet?.cashout && odd.result) return 'win';
      if (odd.result === undefined) return 'pending';
      if (odd.void) return 'void';
      if (odd.cancelled) return 'cancelled';
      if (odd.result) return 'win';
      return 'lose';
    },
    getResultClass(result: string): string {
      switch (result) {
        case 'win':
          return 'text-green-500';
        case 'lose':
          return 'text-red-500';
        case 'void':
          return 'text-blue-500';
        case 'cancelled':
          return 'text-yellow-500';
        default:
          return 'text-yellow-500';
      }
    },
    getOddCurrentScore(odd: BetOdd): string {
      if (
        (odd as any).result_actual &&
        Array.isArray((odd as any).result_actual)
      ) {
        const home = (odd as any).result_actual.find(
          (r: any) => r.Position == '1'
        );
        const away = (odd as any).result_actual.find(
          (r: any) => r.Position == '2'
        );
        if (home || away) {
          return (home ? home.Value : '-') + ' - ' + (away ? away.Value : '-');
        }
      }
      if (
        (odd as any).livescore &&
        (odd as any).livescore.Scoreboard &&
        Array.isArray((odd as any).livescore.Scoreboard.Results)
      ) {
        const home = (odd as any).livescore.Scoreboard.Results.find(
          (r: any) => r.Position == '1'
        );
        const away = (odd as any).livescore.Scoreboard.Results.find(
          (r: any) => r.Position == '2'
        );
        if (home || away) {
          return (home ? home.Value : '-') + ' - ' + (away ? away.Value : '-');
        }
      }
      return '';
    },
    getLivescoreMinute(odd: BetOdd): string | number {
      if (
        (odd as any).livescore &&
        (odd as any).livescore.Scoreboard &&
        typeof (odd as any).livescore.Scoreboard.Time !== 'undefined'
      ) {
        const timeSec = parseInt((odd as any).livescore.Scoreboard.Time, 10);
        if (!isNaN(timeSec) && timeSec >= 0) {
          return Math.floor(timeSec / 60);
        }
        return '';
      }
      if (
        typeof (odd as any).time_actual !== 'undefined' &&
        (odd as any).time_actual !== null &&
        !isNaN(Number((odd as any).time_actual))
      ) {
        const minute = Math.floor(Number((odd as any).time_actual));
        if (minute >= 0) {
          return minute;
        }
        return '';
      }
      return '';
    },
    isMatchInProgress(odd: BetOdd): boolean {
      if (
        odd &&
        ((odd as any).status_event == 2 || (odd as any).status_event === '2')
      ) {
        return true;
      }
      const status =
        this.bet &&
        (this.bet as any).sport_event_status &&
        (this.bet as any).sport_event_status._attributes &&
        (this.bet as any).sport_event_status._attributes.status;
      return status == 2 || status === '2';
    },
    getStatLabel(label: string): string {
      const key = `statistics.${label}`;
      const translation = this.$t(key) as string;
      return translation !== key ? translation : label;
    },
    getStatTypeLabel(type: number, odd: BetOdd): string {
      const sportId =
        (odd && (odd as any).sport && (odd as any).sport.id) ||
        (odd && (odd as any).sport_id) ||
        (this.bet && (this.bet as any).sport_id);
      const mappa = STATISTICS_LABELS[sportId];
      if (!mappa) return 'Stat ' + type;
      return mappa[type] || 'Stat ' + type;
    },
    getStatHomeAwayDisplay(stat: any): string {
      if (!stat || !stat.Results || !Array.isArray(stat.Results)) return '';
      const home = stat.Results.find((r: any) => r.Position == '1');
      const away = stat.Results.find((r: any) => r.Position == '2');
      return (home ? home.Value : '-') + ' - ' + (away ? away.Value : '-');
    },
    statGridRows(odd: BetOdd): any[][] {
      const stats =
        (odd as any).livescore && (odd as any).livescore.Statistics
          ? (odd as any).livescore.Statistics
          : [];
      const maxCols = 6;
      const cells: any[][] = [];
      let row: any[] = [];
      for (let i = 0; i < stats.length; i++) {
        row.push(stats[i]);
        if (row.length === maxCols) {
          cells.push(row);
          row = [];
        }
      }
      if (row.length > 0) {
        while (row.length < maxCols) row.push({});
        cells.push(row);
      }
      return cells;
    },
    getIncRows(period: any): any[][] {
      const maxCols = 6;
      if (!(period && period.Incidents && period.Incidents.length)) return [];
      const rows: any[][] = [];
      let row: any[] = [];
      for (let i = 0; i < period.Incidents.length; i++) {
        const inc = period.Incidents[i];
        row.push({
          key: 'inc-' + i + '-' + inc.IncidentType,
          label: this.getStatTypeLabel(
            inc.IncidentType,
            this.bet && this.bet.odds && this.bet.odds[0]
              ? this.bet.odds[0]
              : (this.bet as any)
          ),
          value: inc.Results && inc.Results[0] ? inc.Results[0].Value : '',
          seconds: inc.Seconds > 0 ? inc.Seconds + 's' : ''
        });
        if (row.length === maxCols) {
          rows.push(row);
          row = [];
        }
      }
      while (row.length > 0 && row.length < maxCols) {
        row.push({
          key: 'empty-' + row.length,
          label: '',
          value: '',
          seconds: ''
        });
      }
      if (row.length > 0) rows.push(row);
      return rows;
    },
    async handleRebet(): Promise<void> {
      if (!this.bet) return;
      this.$emit('close');
      const now = new Date();
      await this.resetCart();
      await this.setOddsInCart(this.bet.odds);
      await this.setCreatedAtCart(now.getTime());
      await this.refreshCart();
      this.$root.$emit('sectionCart');
    },
    handlePrint(): void {
      if (!this.bet || this.isBetCancelled || this.bet.status === 'review')
        return;

      const printUrl = `${this.getPlatformSettings.print_endpoint}${this.bet.code}&type_odd=${this.getOddsType}`;
      window.open(printUrl, '_blank', 'height=600,width=350');
    },
    handleCashoutClick(): void {
      this.$emit('cashout-click', this.bet);
    }
  }
});
